import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiFormHelperText from '@mui/material/FormHelperText';
import * as React from 'react';
import { FlexBox } from '../../box';
import { FilterTextField } from '../../filter-textfield';
import { SimpleSelect } from '../../simple-select';
import { toArray } from '../../utils';
import { findOption, inputToNumber, numberToInput, resolveEditor, selectOptions } from '../utils';
import { Comparator, ValueGroup } from './index';
function getPlaceholder(placeholder, labels) {
    const base = placeholder ?? labels.defaultPlaceholder;
    return base ? `${base}*` : undefined;
}
function getFieldError(error, fieldKey) {
    if (fieldKey && error && typeof error === 'object') {
        return Boolean(error[fieldKey]);
    }
    return error === true;
}
export function ValueEditor(props) {
    const { context, descriptor, hasError, interactionDisabled, labels, setRowValue } = props;
    const editor = resolveEditor(descriptor, context.row.comparator);
    const errorProps = (error, disabled) => !disabled && error
        ? { error: true, helperText: labels.requiredFieldErrorText }
        : {};
    if (!editor) {
        return null;
    }
    if (editor.type === 'ENTITY') {
        const current = context.row.value?.type === 'ENTITY' ? context.row.value.key : null;
        const options = selectOptions(editor.options, [current]);
        const disabled = interactionDisabled || options.length === 0;
        const error = getFieldError(hasError);
        return (_jsx(SimpleSelect, { variant: "primary", "data-role": "filter-value", options: options, value: findOption(editor.options, current), getOptionLabel: (option) => option.label, getOptionKey: (option) => option.key, placeholder: getPlaceholder(editor.placeholder, labels), enableSearch: editor.enableSearch, skeleton: editor.loading, disabled: disabled, ...errorProps(error, disabled), onChange: (option) => setRowValue(context, {
                type: 'ENTITY',
                key: option?.key ?? null
            }) }));
    }
    if (editor.type === 'MULTIPLE_ENTITY') {
        const keys = context.row.value?.type === 'MULTIPLE_ENTITY'
            ? context.row.value.keys
            : [];
        const options = selectOptions(editor.options, keys);
        const disabled = interactionDisabled || options.length === 0;
        const error = getFieldError(hasError);
        return (_jsx(SimpleSelect, { multiple: true, wrapButtonLabel: true, variant: "primary", "data-role": "filter-value", options: options, value: options.filter((option) => keys.includes(option.key)), getOptionLabel: (option) => option.label, getOptionKey: (option) => option.key, tooltip: labels.multipleValueTooltip, tooltipProps: { placement: 'top' }, placeholder: getPlaceholder(editor.placeholder, labels), enableSearch: editor.enableSearch, skeleton: editor.loading, disabled: disabled, ...errorProps(error, disabled), onChange: (options) => setRowValue(context, {
                type: 'MULTIPLE_ENTITY',
                keys: options.map((option) => option.key)
            }) }));
    }
    if (editor.type === 'VALUE') {
        if (context.row.comparator !== 'BETWEEN') {
            const numericValue = context.row.value?.type === 'VALUE'
                ? context.row.value.value
                : null;
            const error = getFieldError(hasError);
            return (_jsx(FilterTextField, { "data-role": "filter-value", value: numberToInput(numericValue), placeholder: getPlaceholder(editor.placeholder, labels), disabled: interactionDisabled, ...errorProps(error, interactionDisabled), onChange: (next) => setRowValue(context, {
                    type: 'VALUE',
                    value: inputToNumber(next)
                }) }));
        }
        const range = context.row.value?.type === 'RANGE'
            ? context.row.value
            : { from: null, to: null };
        const fromError = !interactionDisabled && getFieldError(hasError, 'from');
        const toError = !interactionDisabled && getFieldError(hasError, 'to');
        const rangeError = fromError || toError;
        return (_jsxs(FlexBox, { stack: true, children: [_jsxs(ValueGroup, { children: [_jsx(FilterTextField, { "data-role": "filter-value-from", value: numberToInput(range.from), placeholder: getPlaceholder(editor.placeholder, labels), disabled: interactionDisabled, error: fromError, onChange: (next) => setRowValue(context, {
                                type: 'RANGE',
                                from: inputToNumber(next),
                                to: range.to
                            }) }), _jsx(Comparator, { children: labels.valueComparator }), _jsx(FilterTextField, { "data-role": "filter-value-to", value: numberToInput(range.to), placeholder: getPlaceholder(editor.placeholder, labels), disabled: interactionDisabled, error: toError, onChange: (next) => setRowValue(context, {
                                type: 'RANGE',
                                from: range.from,
                                to: inputToNumber(next)
                            }) })] }), rangeError && (_jsx(MuiFormHelperText, { error: true, "data-role": "filter-value-error", sx: {
                        padding: (theme) => theme.spacing(0.25, 0.75, 0, 0.75)
                    }, children: labels.requiredFieldsErrorText ??
                        labels.requiredFieldErrorText }))] }));
    }
    if (editor.type !== 'COMPOUND') {
        return null;
    }
    const values = context.row.value?.type === 'COMPOUND' ? context.row.value.values : {};
    return (_jsx(ValueGroup, { children: editor.fields.map((field, fieldIndex) => {
            const current = values[field.key];
            const keys = toArray(current ?? undefined) ?? [];
            const options = selectOptions(field.options, keys);
            const disabled = interactionDisabled || options.length === 0;
            const error = getFieldError(hasError, field.key);
            return (_jsxs(React.Fragment, { children: [fieldIndex > 0 && (_jsx(Comparator, { children: labels.valueComparator })), field.type === 'MULTIPLE_ENTITY' ? (_jsx(SimpleSelect, { multiple: true, wrapButtonLabel: true, variant: "primary", "data-role": "filter-value-field", "data-role-value": field.key, options: options, value: options.filter((option) => keys.includes(option.key)), getOptionLabel: (option) => option.label, getOptionKey: (option) => option.key, tooltip: labels.multipleValueTooltip, tooltipProps: { placement: 'top' }, placeholder: getPlaceholder(field.placeholder, labels), enableSearch: field.enableSearch, skeleton: field.loading, disabled: disabled, ...errorProps(error, disabled), onChange: (selectedOptions) => setRowValue(context, {
                            type: 'COMPOUND',
                            values: {
                                ...values,
                                [field.key]: selectedOptions.map(({ key }) => key)
                            }
                        }) })) : (_jsx(SimpleSelect, { variant: "primary", "data-role": "filter-value-field", "data-role-value": field.key, options: options, value: findOption(field.options, keys[0] ?? null), getOptionLabel: (option) => option.label, getOptionKey: (option) => option.key, placeholder: getPlaceholder(field.placeholder, labels), enableSearch: field.enableSearch, skeleton: field.loading, disabled: disabled, ...errorProps(error, disabled), onChange: (option) => setRowValue(context, {
                            type: 'COMPOUND',
                            values: {
                                ...values,
                                [field.key]: option?.key ?? null
                            }
                        }) }))] }, field.key));
        }) }));
}
